import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import {
    lowestResizedRow,
    minimumSpan,
    moveOnScrollingGrid,
    moveRect,
    resizeOnScrollingGrid,
    resizeRect,
    type CellRect,
    type Corner,
    type EditGrid,
} from '@casa/layout';
import type { ScreenTile } from '@casa/model';
import { SELECTED_OUTLINE, TILE_RADIUS } from '@casa/tiles';
import { cellPitch, tileRect, type GridGeometry, type TileRect } from '../screens/grid.ts';
import { TILE_ACTIONS_HEIGHT, TILE_ACTIONS_WIDTH } from './tile_actions.tsx';

// Edit mode on one screen (requirements L-2 and L-5 to L-10 in docs/specs/dashboard-editor.md): a
// layer over the tiles that takes every tap, so tiles do not act. A tap selects the tile on top
// where it lands; a tap on the selected tile selects the next one below it there, so a group laid
// under tiles is reached too. Drags of the selected tile and its corners move and resize it by
// whole cells, with a ghost that follows the finger unsnapped. A gesture is worked out from the
// layout it started from, so dragging back gives that layout again.

/** Around a tile, a tap still selects it (legacy). */
const HIT_MARGIN = 12;
/**
 * A corner's grip is grabbed this close to its centre (legacy). By touch, as wide as a fingertip
 * (issue #10: on a phone, the corners at the screen's edges were almost impossible to grab).
 */
const CORNER_RADIUS = 15;
const TOUCH_CORNER_RADIUS = 26;
/** The grip a corner is dragged by (the owner's sketch, 2026-10-19): a thin bracket just inside the corner, hatched. */
const GRIP_SIZE = 28;
const GRIP_COLOR = '#ff0000';
/** How far the grip's centre is inside the tile from its corner, across and down: clear of the 2 px outline. */
const GRIP_INSET = GRIP_SIZE / 2 + 5;
/** How close to the top or bottom of a scrolling screen a drag scrolls it, and how fast. */
const AUTO_SCROLL_EDGE = 48;
const AUTO_SCROLL_STEP = 12;
/** A pointer that moved this far was a drag, not a tap. */
const DRAG_THRESHOLD = 4;
const CORNERS: readonly Corner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
/** In edit mode a scrolling screen has this many empty rows below its tiles, to place tiles in. */
export const EDIT_ROWS_BELOW = 3;

export interface EditLayerProps {
    tiles: readonly ScreenTile[];
    grid: GridGeometry;
    selectedId: string | undefined;
    onSelect: (id: string | undefined) => void;
    onChange: (tiles: ScreenTile[]) => void;
    /** The buttons for the selection, shown next to it while nothing is dragged. */
    renderActions?: (() => ReactNode) | undefined;
    /** The scrolling element of a scrolling screen; a drag near its edges scrolls it. */
    scroller: HTMLElement | null;
}

interface Gesture {
    pointerId: number;
    index: number;
    corner: Corner | undefined;
    startX: number;
    startY: number;
    /** The tile's rectangle on the screen when the gesture started, for the ghost. */
    startRect: TileRect;
    clientX: number;
    clientY: number;
    before: readonly ScreenTile[];
    /**
     * The layer's height when the gesture started. A scrolling screen keeps at least this height
     * while it lasts: as a tile shrinks the screen would get shorter, the browser would scroll it up
     * under a pointer held still, and the tile would shrink further (issue #39).
     */
    startHeight: number;
    lastKey: string;
    /** Whether the pointer moved far enough to be a drag; the click that follows one is not a tap. */
    hasMoved: boolean;
}

function contains(rect: TileRect, x: number, y: number, margin = 0) : boolean {
    return x >= rect.left - margin && x <= rect.left + rect.width + margin && y >= rect.top - margin && y <= rect.top + rect.height + margin;
}

/** The smallest a tile can be on this grid (L-7). */
function smallestSpan(grid: EditGrid) : { width: number; height: number } {
    return minimumSpan(grid);
}

/** The screen's tiles after the tile of `gesture` has been dragged by `columns` and `rows` cells. */
function draggedLayout(gesture: Gesture, columns: number, rows: number, grid: EditGrid) : ScreenTile[] {
    const tile = gesture.before[gesture.index]!;
    const corner = gesture.corner;
    const moved = corner === undefined;
    let to = moved ? moveRect(tile.layout, columns, rows, grid) : resizeRect(tile.layout, corner, columns, rows, grid, smallestSpan(grid));

    if (grid.rows !== undefined) {
        return gesture.before.map((other, index) => (index === gesture.index ? { ...other, layout: to } : other));
    }

    // A moved tile is limited by `moveOnScrollingGrid`.
    const rects: CellRect[] = gesture.before.map((other) => other.layout);
    const lowest = lowestResizedRow(rects, EDIT_ROWS_BELOW);
    if (!moved && to.y + to.height - 1 > lowest) {
        to = { ...to, height: lowest - to.y + 1 };
    }

    const settled = moved ? moveOnScrollingGrid(rects, gesture.index, to) : resizeOnScrollingGrid(rects, gesture.index, to);

    return gesture.before.map((other, index) => {
        const rect = settled[index]!;
        const same = rect.x === other.layout.x && rect.y === other.layout.y && rect.width === other.layout.width && rect.height === other.layout.height;

        return same ? other : { ...other, layout: { ...rect } };
    });
}

/** Where the finger has the tile, unsnapped: moved by the pointer, or with the dragged corner at it. */
function ghostOf(gesture: Gesture, dx: number, dy: number) : TileRect {
    const { left, top, width, height } = gesture.startRect;
    if (gesture.corner === undefined) {
        return { left: left + dx, top: top + dy, width, height };
    }

    let [x1, y1, x2, y2] = [left, top, left + width, top + height];
    if (gesture.corner.endsWith('left')) {
        x1 = Math.min(x1 + dx, x2);
    } else {
        x2 = Math.max(x2 + dx, x1);
    }
    if (gesture.corner.startsWith('top')) {
        y1 = Math.min(y1 + dy, y2);
    } else {
        y2 = Math.max(y2 + dy, y1);
    }

    return { left: x1, top: y1, width: x2 - x1, height: y2 - y1 };
}

/**
 * `ghost` kept above `bottom`, the end of a scrolling screen: a ghost below it would make the screen
 * taller, so a corner held at the bottom edge would scroll it without end (issue #39).
 */
function ghostAbove(ghost: TileRect, corner: Corner | undefined, bottom: number) : TileRect {
    if (ghost.top + ghost.height <= bottom) {
        return ghost;
    }

    return corner === undefined ? { ...ghost, top: Math.max(0, bottom - ghost.height) } : { ...ghost, height: Math.max(0, bottom - ghost.top) };
}

const layoutKey = (tiles: readonly ScreenTile[]) : string => tiles.map((tile) => `${tile.layout.x},${tile.layout.y},${tile.layout.width},${tile.layout.height}`).join(';');

/** The tiles as they lie on the screen, top first: groups below every other tile, later ones above earlier ones. */
function topFirst(tiles: readonly ScreenTile[]) : number[] {
    const order = [...tiles.keys()].reverse();

    return [...order.filter((index) => tiles[index]!.type !== 'group'), ...order.filter((index) => tiles[index]!.type === 'group')];
}

/**
 * The grip of a corner, drawn for the bottom-left one and turned for the others: a thin bracket
 * along the corner's two sides, and lines across the corner's diagonal, shorter towards the corner,
 * as on a window's resize grip.
 */
function CornerGrip({ corner }: { corner: Corner }) : ReactNode {
    const turn = { 'bottom-left': 0, 'top-left': 90, 'top-right': 180, 'bottom-right': 270 }[corner];
    const hatches = [7, 14.5, 22].map((length) => `M5 ${23 - length} L${5 + length} 23`).join(' ');

    return (
        <svg
            width={GRIP_SIZE}
            height={GRIP_SIZE}
            viewBox="0 0 28 28"
            aria-hidden
            style={{ transform: `rotate(${turn}deg)`, filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.9))', overflow: 'visible' }}
        >
            <path d="M1 0 V27 H28" fill="none" stroke={GRIP_COLOR} strokeWidth="2" />
            {/* <path d={hatches} fill="none" stroke={GRIP_COLOR} strokeWidth="1.75" strokeLinecap="butt" /> */}
        </svg>
    );
}

export function EditLayer(props: EditLayerProps) : ReactNode {
    const { tiles, grid, selectedId, onSelect, renderActions } = props;
    const layer = useRef<HTMLDivElement>(null);
    const gesture = useRef<Gesture | null>(null);
    const scrollFrame = useRef(0);
    const ignoreClick = useRef(false);
    /** Where the last press began: a mouse drag that ends on another tile still sends a click, which is not a tap. */
    const pressedAt = useRef({ x: 0, y: 0 });
    const [isDragging, setDragging] = useState(false);
    const [ghost, setGhost] = useState<TileRect>();
    /** While a gesture lasts on a scrolling screen: the height the screen keeps (`Gesture.startHeight`). */
    const [keptHeight, setKeptHeight] = useState<number>();
    const scrolling = grid.rows === undefined;
    const editGrid: EditGrid = { columns: grid.columns, rows: grid.rows };

    // The latest props, for the pointer handlers and the auto-scroll loop.
    const latest = useRef({ props, grid, editGrid });
    latest.current = { props, grid, editGrid };

    useEffect(() => () => cancelAnimationFrame(scrollFrame.current), []);

    const update = () : void => {
        const current = gesture.current;
        const element = layer.current;
        if (!current || !element) {
            return;
        }

        // Relative to the layer, which scrolls with the tiles: a drag that scrolls the screen moves the tile with it.
        const box = element.getBoundingClientRect();
        const dx = current.clientX - box.left - current.startX;
        const dy = current.clientY - box.top - current.startY;
        const { grid: screenGrid, editGrid: cells, props: latestProps } = latest.current;
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
            current.hasMoved = true;
        }
        if (current.hasMoved) {
            const shown = ghostOf(current, dx, dy);
            setGhost(cells.rows === undefined ? ghostAbove(shown, current.corner, Math.max(element.clientHeight, current.startHeight)) : shown);
        }

        const step = cellPitch(screenGrid);
        const next = draggedLayout(current, Math.round(dx / step.across), Math.round(dy / step.down), cells);
        const key = layoutKey(next);
        if (key !== current.lastKey) {
            current.lastKey = key;
            latestProps.onChange(next);
        }
    };

    const autoScroll = () : void => {
        scrollFrame.current = 0;
        const current = gesture.current;
        const element = latest.current.props.scroller;
        if (!current || !element || latest.current.editGrid.rows !== undefined) {
            return;
        }

        const box = element.getBoundingClientRect();
        const direction = current.clientY < box.top + AUTO_SCROLL_EDGE ? -1 : current.clientY > box.bottom - AUTO_SCROLL_EDGE ? 1 : 0;
        if (direction === 0) {
            return;
        }

        element.scrollTop += direction * AUTO_SCROLL_STEP;
        update();
        scrollFrame.current = requestAnimationFrame(autoScroll);
    };

    const start = (event: ReactPointerEvent, index: number, corner: Corner | undefined, rect: TileRect) : void => {
        const element = layer.current;
        if (!element || gesture.current) {
            return;
        }

        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        const box = element.getBoundingClientRect();
        gesture.current = {
            pointerId: event.pointerId,
            index,
            corner,
            startX: event.clientX - box.left,
            startY: event.clientY - box.top,
            startRect: rect,
            clientX: event.clientX,
            clientY: event.clientY,
            before: tiles,
            startHeight: element.clientHeight,
            lastKey: layoutKey(tiles),
            hasMoved: false,
        };
        setDragging(true);
        setKeptHeight(scrolling ? element.clientHeight : undefined);
    };

    const move = (event: ReactPointerEvent) : void => {
        const current = gesture.current;
        if (!current || current.pointerId !== event.pointerId) {
            return;
        }

        current.clientX = event.clientX;
        current.clientY = event.clientY;
        update();
        if (scrollFrame.current === 0) {
            scrollFrame.current = requestAnimationFrame(autoScroll);
        }
    };

    const end = (event: ReactPointerEvent) : void => {
        const current = gesture.current;
        if (current?.pointerId !== event.pointerId) {
            return;
        }

        gesture.current = null;
        ignoreClick.current = current.hasMoved;
        setDragging(false);
        setGhost(undefined);
        setKeptHeight(undefined);
    };

    /**
     * A tap: the tile on top where it lands; on the selected tile, the next one below it there (so a
     * group under tiles is reached); beside every tile, nothing. A click that ends a drag is not a tap.
     */
    const tap = (event: ReactMouseEvent) : void => {
        if (ignoreClick.current) {
            ignoreClick.current = false;

            return;
        }
        if (Math.abs(event.clientX - pressedAt.current.x) > DRAG_THRESHOLD || Math.abs(event.clientY - pressedAt.current.y) > DRAG_THRESHOLD) {
            return;
        }

        const element = layer.current;
        if (!element) {
            return;
        }

        const box = element.getBoundingClientRect();
        const x = event.clientX - box.left;
        const y = event.clientY - box.top;
        const under = topFirst(tiles).filter((index) => contains(tileRect(tiles[index]!.layout, grid), x, y, HIT_MARGIN)).map((index) => tiles[index]!.id);
        const at = selectedId === undefined ? -1 : under.indexOf(selectedId);

        onSelect(under.length === 0 ? undefined : under[(at + 1) % under.length]);
    };

    const selectedIndex = tiles.findIndex((tile) => tile.id === selectedId);
    const selectedRect = selectedIndex >= 0 ? tileRect(tiles[selectedIndex]!.layout, grid) : undefined;
    const actions = !isDragging && renderActions && selectedRect ? renderActions() : null;
    const radius = window.matchMedia('(pointer: coarse)').matches ? TOUCH_CORNER_RADIUS : CORNER_RADIUS;

    return (
        <div
            ref={layer}
            onPointerDownCapture={(event) => {
                pressedAt.current = { x: event.clientX, y: event.clientY };
                // The click that ends a drag comes before the next press, if at all: a touch drag sends
                // none, and the next tap would be taken for it (issue #40).
                ignoreClick.current = false;
            }}
            onClick={tap}
            style={{ position: 'absolute', inset: 0, touchAction: scrolling ? 'pan-y' : 'none', zIndex: 10 }}
        >
            {tiles.map((tile, index) => {
                const rect = tileRect(tile.layout, grid);
                const isSelected = index === selectedIndex;

                return (
                    // Only the selected tile is dragged (legacy): a drag that starts on another tile
                    // scrolls a scrolling screen, and a tap selects (the layer's click).
                    <div
                        key={tile.id}
                        onPointerDown={isSelected ? (event) => start(event, index, undefined, rect) : undefined}
                        onPointerMove={move}
                        onPointerUp={end}
                        onPointerCancel={end}
                        style={{
                            position: 'absolute',
                            left: rect.left - HIT_MARGIN,
                            top: rect.top - HIT_MARGIN,
                            width: rect.width + 2 * HIT_MARGIN,
                            height: rect.height + 2 * HIT_MARGIN,
                            touchAction: isSelected || !scrolling ? 'none' : 'pan-y',
                            cursor: isSelected ? 'move' : 'pointer',
                            // The selected tile on top, so that it can be dragged where others cover it. Not moved
                            // in the page: that would end the drag.
                            zIndex: isSelected ? 2 : tile.type === 'group' ? 0 : 1,
                        }}
                    >
                        {isSelected && (
                            <>
                                <div style={{ position: 'absolute', inset: HIT_MARGIN, outline: SELECTED_OUTLINE, outlineOffset: -2, borderRadius: TILE_RADIUS, pointerEvents: 'none' }} />
                                {CORNERS.map((corner) => (
                                    <div
                                        key={corner}
                                        onPointerDown={(event) => start(event, index, corner, rect)}
                                        onPointerMove={move}
                                        onPointerUp={end}
                                        onPointerCancel={end}
                                        style={{
                                            position: 'absolute',
                                            width: 2 * radius,
                                            height: 2 * radius,
                                            // Centred on the grip, just inside the corner; the tile's box starts HIT_MARGIN out.
                                            left: corner.endsWith('left') ? HIT_MARGIN + GRIP_INSET - radius : undefined,
                                            right: corner.endsWith('right') ? HIT_MARGIN + GRIP_INSET - radius : undefined,
                                            top: corner.startsWith('top') ? HIT_MARGIN + GRIP_INSET - radius : undefined,
                                            bottom: corner.startsWith('bottom') ? HIT_MARGIN + GRIP_INSET - radius : undefined,
                                            cursor: corner === 'top-left' || corner === 'bottom-right' ? 'nwse-resize' : 'nesw-resize',
                                            touchAction: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CornerGrip corner={corner} />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                );
            })}
            {keptHeight !== undefined && <div style={{ position: 'absolute', top: 0, left: 0, width: 1, height: keptHeight, pointerEvents: 'none' }} />}
            {ghost && (
                <div
                    style={{
                        position: 'absolute',
                        ...ghost,
                        boxSizing: 'border-box',
                        background: 'rgba(255, 255, 255, 0.12)',
                        border: '2px solid rgba(255, 255, 255, 0.6)',
                        borderRadius: TILE_RADIUS,
                        pointerEvents: 'none',
                        zIndex: 5,
                    }}
                />
            )}
            {actions && selectedRect && (
                <div
                    className="chrome"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    style={{
                        position: 'absolute',
                        left: Math.max(0, Math.min(selectedRect.left, grid.width - TILE_ACTIONS_WIDTH)),
                        top: selectedRect.top >= TILE_ACTIONS_HEIGHT + HIT_MARGIN ? selectedRect.top - TILE_ACTIONS_HEIGHT - HIT_MARGIN : selectedRect.top + selectedRect.height + HIT_MARGIN,
                        zIndex: 6,
                    }}
                >
                    {actions}
                </div>
            )}
        </div>
    );
}
